import { inject, Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { map, Observable } from 'rxjs';

import {
  KpiCatalogEntry,
  KpiMaster,
  KpisMastersInput,
  PlayerCompetitionKpi,
  PlayerCompetitionKpisInput,
  PlayerCompetitionPhysical,
  PlayerCompetitionPhysicalInput,
  PlayerGameKpi,
  PlayerGameKpiLatestSeason,
  PlayerGameKpisInput,
  PlayerGameKpisLatestSeasonsInput,
  PlayerRoleKpi,
  PlayerRoleKpisInput,
  TeamCompetitionKpiRow,
  TeamCompetitionKpisInput,
  TeamCompetitionPhysical,
  TeamCompetitionPhysicalInput,
  TeamGameKpiInput,
  TeamGameKpiRow,
  TeamGameKpisFilterInput,
} from '@/core/models/kpi.interface';
import {
  KPI_MASTER_QUERY,
  KPIS_CATALOG_QUERY,
  PLAYER_COMPETITION_KPI_QUERY,
  PLAYER_COMPETITION_PHYSICAL_QUERY,
  PLAYER_GAME_KPI_QUERY,
  PLAYER_GAME_KPIS_LATEST_SEASONS_QUERY,
  PLAYER_ROLE_KPI_QUERY,
  TEAM_COMPETITION_KPIS_FILTERED_QUERY,
  TEAM_COMPETITION_PHYSICAL_QUERY,
  TEAM_GAME_KPIS,
  TEAM_GAME_KPIS_FILTERED_QUERY,
} from '@/core/services/queries/kpi.graphql';
import { TeamGameKpi } from '@/features/pre-game/interfaces/team-data.interface';

@Injectable({
  providedIn: 'root',
})
export class KPIService {
  private readonly apollo = inject(Apollo);

  getKpisMasters(filter: KpisMastersInput): Observable<KpiMaster[]> {
    return this.apollo
      .watchQuery<{ kpisMasters: KpiMaster[] }>({
        query: KPI_MASTER_QUERY,
        variables: {
          filter,
        },
      })
      .valueChanges.pipe(map((result) => result.data.kpisMasters));
  }

  getKpisCatalog(): Observable<KpiCatalogEntry[]> {
    return this.apollo
      .query<{ kpisCatalog: KpiCatalogEntry[] }>({ query: KPIS_CATALOG_QUERY, fetchPolicy: 'network-only' })
      .pipe(map((r) => r.data.kpisCatalog));
  }

  getPlayerGameKpis(filter: PlayerGameKpisInput): Observable<PlayerGameKpi[]> {
    return this.apollo
      .query<{ playerGameKpis: PlayerGameKpi[] }>({
        query: PLAYER_GAME_KPI_QUERY,
        variables: {
          filter,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(map((r) => r.data.playerGameKpis));
  }

  getPlayerGameKpisLatestSeasons(variables: PlayerGameKpisLatestSeasonsInput): Observable<PlayerGameKpiLatestSeason[]> {
    return this.apollo
      .query<{ playerGameKpisLatestSeasons: PlayerGameKpiLatestSeason[] }>({
        query: PLAYER_GAME_KPIS_LATEST_SEASONS_QUERY,
        variables,
        fetchPolicy: 'network-only',
      })
      .pipe(map((r) => r.data.playerGameKpisLatestSeasons));
  }

  getPlayerCompetitionKpis(filter: PlayerCompetitionKpisInput): Observable<PlayerCompetitionKpi[]> {
    return this.apollo
      .query<{ playerCompetitionKpis: PlayerCompetitionKpi[] }>({
        query: PLAYER_COMPETITION_KPI_QUERY,
        variables: {
          filter,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(map((r) => r.data.playerCompetitionKpis));
  }

  getTeamCompetitionKpis(filter: TeamCompetitionKpisInput): Observable<TeamCompetitionKpiRow[]> {
    return this.apollo
      .query<{ teamCompetitionKpis: TeamCompetitionKpiRow[] }>({
        query: TEAM_COMPETITION_KPIS_FILTERED_QUERY,
        variables: { filter },
        fetchPolicy: 'network-only',
      })
      .pipe(map((r) => r.data.teamCompetitionKpis));
  }

  getTeamCompetitionPhysical(filter: TeamCompetitionPhysicalInput): Observable<TeamCompetitionPhysical[]> {
    return this.apollo
      .query<{ teamCompetitionPhysical: TeamCompetitionPhysical[] }>({
        query: TEAM_COMPETITION_PHYSICAL_QUERY,
        variables: { filter },
        fetchPolicy: 'network-only',
      })
      .pipe(map((r) => r.data.teamCompetitionPhysical));
  }

  getPlayerRoleKpis(filter: PlayerRoleKpisInput): Observable<PlayerRoleKpi[]> {
    return this.apollo
      .watchQuery<{ playerRoleKpis: PlayerRoleKpi[] }>({
        query: PLAYER_ROLE_KPI_QUERY,
        variables: { filter },
      })
      .valueChanges.pipe(map((r) => r.data.playerRoleKpis));
  }

  getTeamGameKpis(filter: TeamGameKpiInput): Observable<TeamGameKpi[]> {
    return this.apollo
      .watchQuery<{ teamGameKpis: TeamGameKpi[] }>({
        query: TEAM_GAME_KPIS,
        variables: { filter },
      })
      .valueChanges.pipe(map((r) => r.data.teamGameKpis));
  }

  getTeamGameKpisFiltered(filter: TeamGameKpisFilterInput): Observable<TeamGameKpiRow[]> {
    return this.apollo
      .query<{ teamGameKpisMultiTeam: TeamGameKpiRow[] }>({
        query: TEAM_GAME_KPIS_FILTERED_QUERY,
        variables: { filter },
        fetchPolicy: 'network-only',
      })
      .pipe(map((r) => r.data.teamGameKpisMultiTeam));
  }

  getPlayerCompetitionPhysical(filter: PlayerCompetitionPhysicalInput): Observable<PlayerCompetitionPhysical[]> {
    return this.apollo
      .query<{ playerCompetitionPhysical: PlayerCompetitionPhysical[] }>({
        query: PLAYER_COMPETITION_PHYSICAL_QUERY,
        variables: { filter },
        fetchPolicy: 'network-only',
      })
      .pipe(map((r) => r.data.playerCompetitionPhysical));
  }
}
